import { Portfolio } from "@/types/Portfolio";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useWatchlistAlertMode } from "@/pages/settings/hooks/useWatchlistAlertMode";
import { useHeadlinesFilterStore } from "./HeadlinesFilterStore";
import { useEffect, useRef } from "react";
import { isNotificationMethodEnabled } from "@/types/notifications";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { combinedAlertLists } from "@/shared/utils/alertLists";

type AlertMethod = "sound" | "popup";

/**
 * Alert-store ids selected by the current Watchlist Alert Mode (off/highlight/
 * filter/both). Watchlists are positive, combined watchlists negative — see
 * alertLists.ts.
 */
function useAlertModeIds(): number[] {
  const { watchlistAlertMode } = useWatchlistAlertMode();

  const portfoliosHighlight = useHeadlinesFilterStore(
    (state) => state.portfoliosHighlight
  );
  const portfoliosFilter = useHeadlinesFilterStore(
    (state) => state.portfoliosFilter
  );

  if (watchlistAlertMode === "highlight") return portfoliosHighlight;
  if (watchlistAlertMode === "filter") return portfoliosFilter;
  if (watchlistAlertMode === "both") {
    // Combine and deduplicate
    return [...new Set([...portfoliosHighlight, ...portfoliosFilter])];
  }
  return [];
}

/**
 * Watchlists selected by the current Watchlist Alert Mode, before any method
 * filter. Shared by the sound and pop-up selectors so the alert-mode logic
 * lives in one place.
 */
function useAlertModePortfolios(): Portfolio[] {
  const { portfolios } = usePortfoliosCRUD();
  return useAlertModeIds()
    .map((portfolioId) =>
      portfolios.find((portfolio) => portfolio.id === portfolioId)
    )
    .filter((portfolio): portfolio is Portfolio => portfolio !== undefined);
}

/**
 * Returns a ref of alert-mode watchlists that have the given notification
 * method enabled. Ref so SSE callbacks read current values without re-renders.
 */
function useMethodEnabledRef(method: AlertMethod) {
  const portfolios = useAlertModePortfolios();
  const filtered = portfolios.filter((portfolio) =>
    isNotificationMethodEnabled(portfolio.notifications, method)
  );

  const ref = useRef(filtered);
  useEffect(() => {
    ref.current = filtered;
  }, [filtered]);

  return ref;
}

/**
 * Hook that tracks portfolios with sound alerts enabled for Headlines, gated by
 * the user's Watchlist Alert Mode (off/highlight/filter/both).
 */
export function useHeadlinesPortfoliosWithSoundEnabled() {
  return useMethodEnabledRef("sound");
}

/**
 * Hook that tracks portfolios with pop-up (browser notification) alerts enabled
 * for Headlines, gated by the same Watchlist Alert Mode.
 */
export function useHeadlinesPortfoliosWithPopupEnabled() {
  return useMethodEnabledRef("popup");
}

/**
 * The combined watchlists behind the same alert mode with `method` switched
 * on, matched in full (tags included) by the caller. Ref for the same reason
 * as the watchlist selectors.
 */
function useCombinedMethodEnabledRef(method: AlertMethod) {
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const lists = combinedAlertLists(useAlertModeIds(), queriesQuery.data, method);

  const ref = useRef(lists);
  useEffect(() => {
    ref.current = lists;
  }, [lists]);

  return ref;
}

export function useHeadlinesCombinedWithSoundEnabled() {
  return useCombinedMethodEnabledRef("sound");
}

export function useHeadlinesCombinedWithPopupEnabled() {
  return useCombinedMethodEnabledRef("popup");
}
